import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { MemberShipCardUsageItem } from "../../../../types/memberShipCardTypes";

import { getMemberShipUsage } from "../../../../services/memberShipCardServices";

import useSimplePopup from "../../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../../hooks/useFullScreenLoading";

import StickyHeader from "../../../../components/Layout/StickyHeader";
import CustomDatePicker from "../../../../components/Input/DatePicker";
import {
  ComboButtonGroup,
  ComboButtonOption,
  DateRangeOption,
} from "../../../Manager/Report";
import HorizontalRule from "../../../../components/Rule/Horizontal";
import UsageItem from "./UsageItem";
import { getUsageAmount } from "../../../../libs/ourhome";

type Props = {};

export default function MemberShipCardUsage(props: Props) {
  const navigate = useNavigate();
  const [startDate, setStartDate] = React.useState<Date>(new Date());
  const [endDate, setEndDate] = React.useState<Date>(new Date());
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [data, setData] = useState<MemberShipCardUsageItem[]>([]);

  const getDateRangeOption = (): DateRangeOption[] => {
    const today = new Date();

    const thisWeekStart = new Date(today);
    thisWeekStart.setDate(today.getDate() - 6);
    thisWeekStart.setHours(0, 0, 0, 0); // 이번 주의 시작일로 설정 (일요일 시작 기준)
    const thisWeekEnd = new Date(today);
    thisWeekEnd.setDate(today.getDate());
    thisWeekEnd.setHours(23, 59, 59, 999); // 이번 주의 마지막 날로 설정

    const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1); // 이번 달의 시작일로 설정
    const thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    thisMonthEnd.setHours(23, 59, 59, 999); // 이번 달의 마지막 날로 설정

    const prevMonthStart = new Date(
      today.getFullYear(),
      today.getMonth() - 1,
      1
    ); // 이번 달의 시작일로 설정
    const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    thisMonthEnd.setHours(23, 59, 59, 999); // 이번 달의 마지막 날로 설정

    return [
      {
        title: "일주일",
        createdAtStart: thisWeekStart,
        createdAtEnd: thisWeekEnd,
      },
      {
        title: "이번달",
        createdAtStart: thisMonthStart,
        createdAtEnd: thisMonthEnd,
      },
      {
        title: "지난달",
        createdAtStart: prevMonthStart,
        createdAtEnd: prevMonthEnd,
      },
    ];
  };

  const dateRangeOption = getDateRangeOption();

  const isSameDate = (date1?: Date, date2?: Date) => {
    if (!date1 || !date2) return false;
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const dateToString = (date?: Date) => {
    return `${date?.getFullYear()}${((date?.getMonth() ?? 0) + 1)
      .toString()
      .padStart(2, "0")}${date?.getDate().toString().padStart(2, "0")}`;
  };

  const loadMemberShipUsage = async () => {
    setIsLoading(true);
    try {
      const res = await getMemberShipUsage({
        startDate: dateToString(startDate),
        endDate: dateToString(endDate),
      });
      if (res.status === HttpStatusCode.Ok) {
        setData(res.data);
      } else {
        setData([]);
      }
    } catch (e: any) {
      if (e?.response?.data?.errorCode) {
        open(e?.response?.data?.errorCode);
      } else {
        open("멤버십 이용내역 로드 실패");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMemberShipUsage();
  }, [startDate, endDate]);

  return (
    <div className="flex flex-col gap-5">
      {loadingComponent}
      <StickyHeader
        title="이용내역"
        onBackClick={() => navigate("/service/membership-card")}
        className="px-5"
      />

      <div className="flex flex-col gap-5">
        <div className="flex px-5 gap-2.5 items-center">
          <CustomDatePicker
            date={startDate}
            maxDate={new Date()}
            startDate={startDate}
            endDate={endDate}
            onChange={(date) => {
              if (date) {
                // endDate의 한달전일 경우에만 setStartDate
                const minStart = new Date(
                  endDate.getFullYear(),
                  endDate.getMonth() - 1,
                  endDate.getDate()
                );
                if (minStart < date) {
                  setStartDate(date);
                } else {
                  open("이용 내역 조회는\n1개월까지 가능해요", {
                    title: "알림",
                    unUseHorizonRule: true,
                    icon: "notice",
                  });
                }
              }
            }}
            dateFormat="yy년 MM월 dd일"
          />
          <div>~</div>
          <CustomDatePicker
            date={endDate}
            maxDate={new Date()}
            startDate={startDate}
            endDate={endDate}
            onChange={(date) => {
              if (date) {
                const maxEnd = new Date(
                  startDate.getFullYear(),
                  startDate.getMonth() - 1,
                  startDate.getDate()
                );
                if (date < maxEnd) {
                  setEndDate(date);
                } else {
                  open("이용 내역 조회는\n1개월까지 가능해요", {
                    title: "알림",
                    unUseHorizonRule: true,
                    icon: "notice",
                  });
                }
              }
            }}
            dateFormat="yy년 MM월 dd일"
          />
        </div>

        <div className="px-5 pb-5 flex flex-col gap-2.5">
          <ComboButtonGroup
            options={dateRangeOption}
            selectedOption={dateRangeOption.find(
              (item) =>
                isSameDate(item?.createdAtStart, startDate) &&
                isSameDate(item?.createdAtEnd, endDate)
            )}
            onClick={(option: ComboButtonOption) => {
              setStartDate(option?.createdAtStart ?? new Date());
              setEndDate(option?.createdAtEnd ?? new Date());
            }}
          />
          <div className="text-sm leading-[18px] text-brand-neutral-50">
            ※ 최대 조회기간은 1개월까지 가능합니다.
          </div>
        </div>

        <div className="flex flex-col gap-2.5">
          <div className="pt-2.5 px-5 flex flex-col gap-5">
            <div className="flex justify-between">
              <div>총 {data?.length ?? 0}건</div>
              <div>
                {(
                  data.reduce((prev, cur) => {
                    return prev + getUsageAmount(cur.usageType, cur?.amount);
                  }, 0) ?? 0
                ).toLocaleString()}
                원
              </div>
            </div>
            <HorizontalRule className="bg-black" margin="0" />
          </div>

          {data.map((item) => (
            <UsageItem item={item} />
          ))}
        </div>
      </div>

      {simplePopupComponent}
    </div>
  );
}
